<template>
  <div class="row">
    <div class="flex xs10 offset--xs1">
      <va-card :title="title">
        <div class="row">
          <!-- ID 显示 (仅编辑模式) -->
          <div class='flex md6 sm6 xs12' v-show="showId">
            <va-input
              v-model='insertData.id'
              label='ID'
              disabled
            />
          </div>

          <!-- Slug -->
          <div class="flex md6 sm6 xs12">
            <va-input
              v-model="insertData.slug"
              label="URL Slug"
              placeholder="例: my-first-blog (小写字母和连字符)"
              removable
              :messages="message"
              :error="!!error.slugErrors.length"
              :error-messages="error.slugErrors"
              @input="formatSlug"
            />
          </div>

          <!-- 标题 -->
          <div class="flex md6 sm6 xs12">
            <va-input
              v-model="insertData.title"
              label="博客标题"
              placeholder="请输入标题"
              removable
              :messages="message"
              :error="!!error.titleErrors.length"
              :error-messages="error.titleErrors"
            />
          </div>

          <!-- 作者 -->
          <div class="flex md6 sm6 xs12">
            <va-input
              v-model="insertData.author"
              label="作者"
              placeholder="请输入作者名字"
              removable
              :messages="message"
              :error="!!error.authorErrors.length"
              :error-messages="error.authorErrors"
            />
          </div>

          <!-- 摘要 -->
          <div class="flex md12 sm6 xs12">
            <va-input
              v-model="insertData.summary"
              label="摘要"
              placeholder="请输入博客摘要（可选）"
              removable
              type="textarea"
              :min-rows="2"
              :max-rows="4"
            />
          </div>

          <!-- 封面图预览 -->
          <div class='flex md12 sm6 xs12' v-show="insertData.coverUrl.length !== 0">
            <va-card
              overlay
              :image='insertData.coverUrl'
              class="va-card-image"
            >
            </va-card>
          </div>

          <!-- 封面图上传 -->
          <div class='flex md12 sm6 xs12'>
            <fieldset class="upload-fieldset">
              <legend>封面图上传</legend>
              <input
                type="file"
                ref="fileInput"
                accept="image/*"
                style="display: none;"
                @change="handleFileChange"
              />
              <va-button
                small
                icon="fa fa-upload"
                @click="$refs.fileInput.click()"
              >
                选择图片
              </va-button>
              <span v-if="insertData.coverUrlArr.length > 0" class="file-name">
                {{ insertData.coverUrlArr[0].name }}
              </span>
            </fieldset>
          </div>

          <!-- 封面图 Alt 文本 -->
          <div class="flex md12 sm6 xs12">
            <va-input
              v-model="insertData.coverAlt"
              label="封面图 Alt 文本"
              placeholder="图片的替代文本，用于 SEO 和无障碍访问"
              removable
            />
          </div>

          <!-- 博客内容 (富文本编辑器) -->
          <div class="flex md12 sm12 xs12">
            <div class="editor-label">博客内容</div>
            <editor
              v-model="insertData.content"
              :init="tinymceInit"
            />
          </div>

          <!-- 创建时间 (仅编辑模式显示) -->
          <div class="flex md4 sm6 xs12" v-show="insertData.createTime">
            <va-input
              v-model="insertData.createTime"
              label="创建时间"
              disabled
            />
          </div>

          <!-- SEO 标题 -->
          <div class="flex md6 sm6 xs12">
            <va-input
              v-model="insertData.seoTitle"
              label="SEO 标题"
              placeholder="SEO 优化标题（可选）"
              removable
            />
          </div>

          <!-- SEO 描述 -->
          <div class="flex md6 sm6 xs12">
            <va-input
              v-model="insertData.seoDescription"
              label="SEO 描述"
              placeholder="SEO 优化描述（可选）"
              removable
            />
          </div>

          <!-- 关键词 -->
          <div class="flex md12 sm6 xs12">
            <va-input
              v-model="insertData.keywords"
              label="关键词"
              placeholder="多个关键词用英文逗号分隔，例: Java,Spring Boot,Tutorial"
              removable
            />
          </div>

          <!-- 禁止搜索引擎索引 -->
          <div class="flex md6 sm6 xs12">
            <va-checkbox
              v-model="noIndexChecked"
              label="禁止搜索引擎索引 (noindex)"
            />
          </div>
        </div>

        <!-- 提交按钮 -->
        <div class="row align--center justify--center">
          <div class="flex md2 sm6 xs12">
            <va-button
              class="ma-2"
              color="gray"
              outline
              @click="clickSubmit(0)"
              :disabled="!submitButtonStatus"
            >
              保存草稿
            </va-button>
          </div>
          <div class="flex md2 sm6 xs12">
            <va-button
              class="ma-2"
              color="primary"
              @click="clickSubmit(1)"
              :disabled="!submitButtonStatus"
            >
              发布
            </va-button>
          </div>
        </div>
      </va-card>
    </div>
  </div>
</template>

<script>
import Editor from '@tinymce/tinymce-vue'
import tinymce from 'tinymce/tinymce'
import 'tinymce/themes/silver'
import 'tinymce/icons/default'
import 'tinymce/plugins/table'
import 'tinymce/plugins/lists'
import 'tinymce/plugins/link'
import 'tinymce/plugins/image'
import 'tinymce/plugins/code'
import 'tinymce/plugins/codesample'
import 'tinymce/plugins/fullscreen'
import 'tinymce/plugins/media'
import 'tinymce/plugins/wordcount'
import 'tinymce/plugins/paste'
import uploadApi from '../../api/upload'
import blogApi from '../../api/blog'
import { copyObject, formatTime } from '../../utils/common'

export default {
  components: {
    Editor,
  },
  data () {
    return {
      tinymceInit: {
        height: 500,
        language: 'zh_CN',
        language_url: '/tinymce/langs/zh_CN.js',
        skin_url: '/tinymce/skins/ui/oxide',
        content_css: '/tinymce/skins/content/default/content.css',
        plugins: 'table lists link image code codesample fullscreen media wordcount paste',
        toolbar: 'undo redo | formatselect | bold italic underline strikethrough | forecolor backcolor | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | table | link image media | codesample code | fullscreen',
        menubar: 'file edit view insert format tools table',
        branding: false,
        promotion: false,
        convert_urls: false,
        paste_data_images: true,
        automatic_uploads: true,
        images_upload_handler: (blobInfo, success, failure) => {
          const formData = new FormData()
          formData.append('type', 'image')
          formData.append('file', blobInfo.blob(), blobInfo.filename())
          uploadApi.uploadImage(formData).then(response => {
            success(response.url)
          }).catch(error => {
            failure(error.msg || '图片上传失败')
          })
        },
        codesample_languages: [
          { text: 'HTML/XML', value: 'markup' },
          { text: 'JavaScript', value: 'javascript' },
          { text: 'CSS', value: 'css' },
          { text: 'JSON', value: 'json' },
          { text: 'Java', value: 'java' },
          { text: 'Python', value: 'python' },
          { text: 'SQL', value: 'sql' },
          { text: 'Bash', value: 'bash' },
        ],
      },
      insertData: {
        id: null,
        slug: '',
        title: '',
        author: '',
        summary: '',
        content: '',
        coverUrl: '',
        coverAlt: '',
        coverUrlArr: [],
        status: 0,  // 0-草稿 1-已发布
        publishTime: null,
        seoTitle: '',
        seoDescription: '',
        keywords: '',
        noIndex: 0,  // 0-允许索引 1-禁止索引
        createTime: '',
      },
      message: ['Required field'],
      error: {
        slugErrors: [],
        titleErrors: [],
        authorErrors: [],
      },
      showId: false,
      title: '创建博客',
      submitButtonStatus: true,
    }
  },
  created () {
    this.getBlog()
  },
  computed: {
    formReady () {
      return !this.error.slugErrors.length &&
          !this.error.titleErrors.length &&
          !this.error.authorErrors.length
    },
    noIndexChecked: {
      get () {
        return this.insertData.noIndex === 1
      },
      set (val) {
        this.insertData.noIndex = val ? 1 : 0
      },
    },
  },
  methods: {
    getBlog () {
      const params = this.$route.params
      if (params.id != null) {
        this.showId = true
        this.title = '编辑博客'
        // 通过id获取详情
        blogApi.getBlog(params.id).then(response => {
          copyObject(response, this.insertData)
          this.insertData.createTime = formatTime(this.insertData.createTime)
        }).catch(error => {
          this.showToast(error.msg || '获取博客详情失败', {
            duration: 1000,
            icon: 'fa-warning',
            position: 'top-center',
            fullWidth: false,
          })
        })
      }
    },
    clickSubmit (status) {
      this.checkField()
      if (!this.formReady) {
        return
      }
      this.changeButtonStatus(false)

      // 准备提交数据
      const data = {
        slug: this.insertData.slug,
        title: this.insertData.title,
        author: this.insertData.author,
        summary: this.insertData.summary || '',
        content: this.insertData.content || '',
        coverUrl: this.insertData.coverUrl || '',
        coverAlt: this.insertData.coverAlt || '',
        status: status,  // 使用按钮传入的状态
        seoTitle: this.insertData.seoTitle || '',
        seoDescription: this.insertData.seoDescription || '',
        keywords: this.insertData.keywords || '',
        noIndex: this.insertData.noIndex,
      }

      // 处理发布时间：发布时使用当前时间
      if (status === 1) {
        data.publishTime = Math.floor(Date.now() / 1000)  // 使用当前时间（秒级时间戳）
      }

      if (this.insertData.id != null) {
        // 编辑模式
        blogApi.updateBlog(this.insertData.id, data).then(response => {
          if (response.success === true) {
            this.showToast('更新博客成功', {
              duration: 1000,
              icon: 'fa-check',
              position: 'top-center',
              fullWidth: false,
            })
            this.$router.push({ name: 'listBlog' })
          }
        }).catch(error => {
          this.changeButtonStatus(true)
          this.showToast(error.msg || '更新博客失败', {
            duration: 1000,
            icon: 'fa-warning',
            position: 'top-center',
            fullWidth: false,
          })
        })
      } else {
        // 新增模式
        blogApi.saveBlog(data).then(response => {
          if (response.success === true) {
            this.showToast('创建博客成功', {
              duration: 1000,
              icon: 'fa-check',
              position: 'top-center',
              fullWidth: false,
            })
            this.$router.push({ name: 'listBlog' })
          }
        }).catch(error => {
          this.changeButtonStatus(true)
          this.showToast(error.msg || '创建博客失败', {
            duration: 1000,
            icon: 'fa-warning',
            position: 'top-center',
            fullWidth: false,
          })
        })
      }
    },
    checkField () {
      // Slug 验证：必填 + 格式检查
      if (!this.insertData.slug) {
        this.error.slugErrors = ['slug is required']
      } else if (!/^[a-z0-9-]+$/.test(this.insertData.slug)) {
        this.error.slugErrors = ['slug 只能包含小写字母、数字和连字符']
      } else {
        this.error.slugErrors = []
      }

      this.error.titleErrors = this.insertData.title ? [] : ['title is required']
      this.error.authorErrors = this.insertData.author ? [] : ['author is required']
    },
    formatSlug () {
      // 自动格式化 slug: 转小写，空格转连字符，只保留字母数字连字符
      this.insertData.slug = this.insertData.slug
        .toLowerCase()
        .replace(/\s+/g, '-')
        .replace(/[^a-z0-9-]/g, '')
    },
    handleFileChange (event) {
      const files = event.target.files
      if (files && files.length > 0) {
        this.insertData.coverUrlArr = Array.from(files)
        this.uploadImage()
      }
    },
    uploadImage () {
      if (this.insertData.coverUrlArr.length === 0) return
      const formData = new FormData()
      formData.append('type', 'image')
      formData.append('file', this.insertData.coverUrlArr[this.insertData.coverUrlArr.length - 1])
      uploadApi.uploadImage(formData).then(response => {
        this.insertData.coverUrl = response.url
        this.showToast('上传封面图成功', {
          duration: 1000,
          icon: 'fa-check',
          position: 'top-center',
          fullWidth: false,
        })
      }).catch(error => {
        this.showToast(error.msg || '上传封面图失败', {
          duration: 1000,
          icon: 'fa-warning',
          position: 'top-center',
          fullWidth: false,
        })
      })
    },
    changeButtonStatus (status) {
      this.submitButtonStatus = status
    },
  },
}

</script>

<style lang="scss" scoped>
.va-card-image {
  height: 200px;
  width: 200px;
  border-radius: 5%;
  margin-bottom: 10px;
}

.upload-fieldset {
  width: 100%;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 16px;

  legend {
    padding: 0 8px;
    font-size: 14px;
    font-weight: 500;
    color: #666666;
  }

  .file-name {
    margin-left: 12px;
    font-size: 14px;
    color: #333333;
  }
}

.editor-label {
  font-size: 14px;
  font-weight: 500;
  color: #666666;
  margin-bottom: 8px;
}
</style>
